import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { PlaySquare, Plus, MoreVertical, Trash2, Loader2, Calendar } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso; }
};

export default function Players() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", description: "" });

  const load = async () => {
    try { setPlayers((await api.get("/players")).data); }
    catch (e) { toast.error("Failed to load players"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [user?.role]);

  const handleCreate = async () => {
    if (!form.name.trim()) { toast.error("Player name is required"); return; }
    setCreating(true);
    try {
      const res = await api.post("/players", form);
      toast.success("Player created");
      setCreateOpen(false);
      setForm({ name: "", description: "" });
      navigate(`/players/${res.data.id}`);
    } catch (e) { toast.error("Failed to create player"); }
    finally { setCreating(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/players/${deleteTarget.id}`);
      toast.success("Player deleted");
      setPlayers((p) => p.filter((x) => x.id !== deleteTarget.id));
    } catch (e) { toast.error("Failed to delete"); }
    finally { setDeleteTarget(null); }
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1400px] mx-auto">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Players</h1>
          <p className="text-gray-400 mt-1.5 text-sm">Configure embeddable video players — playback, ads, security and branding.</p>
        </div>
        <button data-testid="create-player-button" onClick={() => setCreateOpen(true)} className="gold-gradient hover:brightness-110 text-black font-semibold px-4 py-2.5 rounded-xl flex items-center gap-2 shadow-lg shadow-amber-500/20 transition-all active:scale-[0.98] w-max cursor-pointer">
          <Plus className="h-4 w-4" /> Create Player
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-32"><Loader2 className="h-6 w-6 text-amber-400 animate-spin" /></div>
      ) : players.length === 0 ? (
        <div className="text-center py-24 border border-dashed border-gray-800 rounded-2xl">
          <div className="h-14 w-14 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mx-auto"><PlaySquare className="h-7 w-7 text-amber-400" /></div>
          <p className="mt-4 text-white font-medium">No players yet</p>
          <p className="text-gray-500 text-sm mt-1">Create your first player to start embedding streams.</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {players.map((pl, i) => (
            <motion.div key={pl.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.05, 0.4) }}
              data-testid={`player-card-${pl.id}`} onClick={() => navigate(`/players/${pl.id}`)}
              className="group bg-card border border-gray-800 hover:border-amber-500/40 rounded-2xl p-5 transition-all cursor-pointer relative overflow-hidden">
              <div className="absolute -right-16 -top-16 h-32 w-32 rounded-full bg-amber-500/5 blur-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="flex items-start justify-between relative">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-11 w-11 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center shrink-0"><PlaySquare className="h-5 w-5 text-amber-400" /></div>
                  <div className="min-w-0">
                    <p className="font-semibold text-white truncate">{pl.name}</p>
                    <p className="text-xs text-gray-500 truncate">{pl.description || "No description"}</p>
                  </div>
                </div>
                <div onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button data-testid={`player-menu-${pl.id}`} className="h-8 w-8 rounded-lg text-gray-500 hover:text-white hover:bg-gray-800 flex items-center justify-center transition-colors"><MoreVertical className="h-4 w-4" /></button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-card border-gray-800 text-gray-200">
                      <DropdownMenuItem onClick={() => navigate(`/players/${pl.id}`)} className="cursor-pointer focus:bg-gray-800">Configure</DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-gray-800" />
                      <DropdownMenuItem onClick={() => setDeleteTarget(pl)} className="cursor-pointer text-red-400 focus:bg-red-500/10 focus:text-red-400"><Trash2 className="h-4 w-4 mr-2" /> Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-4 relative">
                <span className={"inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border " + (pl.active ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : "bg-gray-500/15 text-gray-400 border-gray-500/30")}>
                  <span className={"h-1.5 w-1.5 rounded-full " + (pl.active ? "bg-emerald-400" : "bg-gray-500")} />
                  {pl.active ? "Active" : "Inactive"}
                </span>
                {user?.role === "admin" && <span className="text-[10px] text-gray-600 truncate max-w-[110px] ml-auto">{pl.owner_name}</span>}
              </div>
              <div className="mt-4 pt-4 border-t border-gray-800/70 flex items-center gap-1.5 text-xs text-gray-500 relative">
                <Calendar className="h-3.5 w-3.5" /> Created {fmtDate(pl.created_at)}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-card border-gray-800 text-white">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Create Player</DialogTitle>
            <DialogDescription className="text-gray-400">A configurable player with sensible defaults will be created.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-gray-300">Player Name</Label>
              <Input data-testid="new-player-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Default Player" className="bg-gray-950 border-gray-800 focus-visible:ring-amber-500/40" />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Description</Label>
              <Textarea data-testid="new-player-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="bg-gray-950 border-gray-800 focus-visible:ring-amber-500/40 resize-none" />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setCreateOpen(false)} className="px-4 py-2 rounded-lg text-gray-400 hover:text-white text-sm">Cancel</button>
            <button data-testid="submit-create-player" onClick={handleCreate} disabled={creating} className="gold-gradient text-black font-semibold px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-60 cursor-pointer">
              {creating && <Loader2 className="h-4 w-4 animate-spin" />} Create Player
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-card border-gray-800 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this player?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">"{deleteTarget?.name}" and its configuration will be permanently removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-gray-700 text-gray-300 hover:bg-gray-800 hover:text-white">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-player" onClick={handleDelete} className="bg-red-600 hover:bg-red-500 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
